import { Badge, useTranslation } from "@ncino/pax-ui";
import Tooltip from "@mui/material/Tooltip";
import type {
  PaxSidebarProductDetails,
  PaxSidebarNavItem,
  PaxSidebarDrawerWidth,
} from "@ncino/pax-ui";
import { useNavigate } from "@tanstack/react-router";
import type { LinkProps } from "@tanstack/react-router";
import { useCanViewSettingsQuery } from "@/services/auth/permissions";
import { useEffectivePortfolioId } from "@/shared/hooks/useActivePortfolio";
import { useMyPortfoliosQuery } from "@/services/graphql/reactQuery";
import {
  UserIcon,
  HouseLineIcon,
  ShoppingCartIcon,
  TagIcon,
  ChartLineUpIcon,
  QuestionIcon,
  GearIcon,
  CheckCircleIcon,
} from "@phosphor-icons/react";

type AppRoute = LinkProps["to"];

export const SIDEBAR_WIDTH_EXPANDED = 256;
export const SIDEBAR_WIDTH_COLLAPSED = 75;

interface SidebarConfig {
  readonly AppDetails: PaxSidebarProductDetails;
  readonly drawerWidth: PaxSidebarDrawerWidth;
  readonly topButtons: PaxSidebarNavItem[];
  readonly botButtons: PaxSidebarNavItem[];
}

export function useSidebarConfig(isExpanded: boolean): SidebarConfig {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data: canViewSettings } = useCanViewSettingsQuery();

  const { portfolioId: activePortfolioId } = useEffectivePortfolioId();
  const { data: myPortfoliosData } = useMyPortfoliosQuery();
  const isAllAccess = myPortfoliosData?.getMyPortfolios?.isAllAccess ?? false;
  const activeInstitution = myPortfoliosData?.getMyPortfolios?.institutions.find(
    (i) => i.primaryLoanPortfolioId === activePortfolioId
  );

  const navItem = (
    to: AppRoute,
    rest: Omit<PaxSidebarNavItem, "link" | "onClick">
  ): PaxSidebarNavItem => ({
    ...rest,
    link: to as string,
    onClick: () => navigate({ to }),
  });

  const portfolioName =
    activeInstitution?.nameShort ?? activeInstitution?.instName ?? t("sidebar.portfolios");
  const portfolioIcon = isExpanded ? (
    <UserIcon />
  ) : (
    <Tooltip title={portfolioName} placement="right" arrow>
      <UserIcon />
    </Tooltip>
  );
  const portfolioDetails: Omit<PaxSidebarNavItem, "link" | "onClick"> = {
    name: portfolioName,
    shortName: t("sidebar.portfolioCollapsed"),
    expandedTooltip: activeInstitution?.instName,
    icon: portfolioIcon,
  };
  const portfolioNavItem: PaxSidebarNavItem = isAllAccess
    ? navItem("/portfolios", portfolioDetails)
    : portfolioDetails;

  return {
    AppDetails: {
      shortName: t("sidebar.shortName"),
      longName: t("sidebar.longName"),
    },
    drawerWidth: {
      expanded: SIDEBAR_WIDTH_EXPANDED,
      collapsed: SIDEBAR_WIDTH_COLLAPSED,
    },
    topButtons: [
      navItem("/", {
        name: t("sidebar.home"),
        shortName: t("sidebar.home"),
        icon: <HouseLineIcon />,
      }),
      navItem("/buy", { name: t("sidebar.buy"), icon: <ShoppingCartIcon /> }),
      navItem("/sell", { name: t("sidebar.sell"), icon: <TagIcon /> }),
      navItem("/manage", { name: t("sidebar.manage"), icon: <ChartLineUpIcon /> }),
      //navItem("/institutions", { name: t("sidebar.institutions"), icon: <BusinessRounded /> }),
    ],
    botButtons: [
      navItem("/tasks", {
        name: t("sidebar.tasks"),
        shortName: t("sidebar.tasks"),
        icon: (
          // Demo badge — replace badgeContent with real notification count
          <Badge color="primary" badgeContent={0} overlap="circular">
            <CheckCircleIcon />
          </Badge>
        ),
      }),
      navItem("/help", { name: t("sidebar.help"), icon: <QuestionIcon /> }),
      ...(canViewSettings
        ? [navItem("/settings", { name: t("sidebar.settings"), icon: <GearIcon /> })]
        : []),
      portfolioNavItem,
    ],
  };
}
